import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { codeProductData } from './rawData/codeProductData';
import {
  ProductComplete,
  CodebaseProductData,
  ShopifyProductData,
  LocalisedCodebaseProductData,
} from '../types/models';
import merge from 'deepmerge';
import { getCodeProductBlank } from '../services/productBlanks';
import { useStoreMediaLocaliseHelper } from '../composables/storeMediaLocaliseHelper';
import { useStoreContentLocaliseHelper } from '../composables/storeContentLocaliseHelper';
import { createProductMapKey } from '@/composables/productHelper';

// Helper function for validation - extract out
const validateProductAttributes = (product: ShopifyProductData): void => {
  const { sku, handle } = product.attributes || {};
  if (!sku || !handle) {
    throw new Error(`Product missing required attributes (sku: ${sku}, handle: ${handle})`);
  }
};

export const useProductBaseStore = defineStore('productBaseStore', () => {
  const cachedBaseProducts = ref<ShopifyProductData[]>([]);
  const cachedNestedProducts = ref<ShopifyProductData[]>([]);

  const setCachedBaseProducts = (products: ShopifyProductData[]) => {
    try {
      // Could this be cleaer in checking if is array?
      if (!Array.isArray(products)) {
        throw new Error('must be an array');
      }

      cachedBaseProducts.value = products;
    } catch (error) {
      console.error('Failed to set cached base products:', error);
      throw error;
    }
  };

  const setCachedNestedProducts = (products: ShopifyProductData[]) => {
    try {
      if (!Array.isArray(products)) {
        throw new Error('Must be in an array');
      }
      cachedNestedProducts.value = products;
    } catch (err) {
      console.error(err);
    }
  };

  const buildCompleteProduct = (cachedBaseProduct: ShopifyProductData): ProductComplete => {
    try {
      validateProductAttributes(cachedBaseProduct);

      const { sku, handle } = cachedBaseProduct.attributes;
      const mapKey = createProductMapKey(sku, handle);

      const codebaseProduct: CodebaseProductData =
        codeProductData.get(mapKey) || getCodeProductBlank(sku);

      let localisedMedia;
      try {
        localisedMedia = useStoreMediaLocaliseHelper(codebaseProduct.media);
      } catch (error) {
        console.warn(`Failed to localise media for product ${handle}:`, error);
        // Do we want a default here for localise?
      }

      const localisedContent = useStoreContentLocaliseHelper(codebaseProduct.content);

      const localisedCodebaseProduct: LocalisedCodebaseProductData = {
        ...codebaseProduct,
        media: localisedMedia,
        content: localisedContent,
      };

      const completeProduct = merge<ProductComplete>(
        cachedBaseProduct as Partial<ProductComplete>,
        localisedCodebaseProduct as Partial<ProductComplete>,
      );

      completeProduct.attributes.soldOut =
        cachedBaseProduct.attributes.soldOut === true ||
        (cachedBaseProduct.attributes.soldOut as unknown as string) === 'true';

      return completeProduct;
    } catch (error) {
      const handle = cachedBaseProduct?.attributes?.handle || 'unknown';
      // Do we check if error message exists
      throw new Error(`Failed to build product for handle "${handle}": ${error.message}`);
    }
  };

  const allCachedProducts = computed<ShopifyProductData[]>(() => [
    ...cachedBaseProducts.value,
    ...cachedNestedProducts.value,
  ]);

  const completeBaseProducts = computed<ProductComplete[]>(() => {
    const results: ProductComplete[] = [];

    for (const cachedBaseProduct of allCachedProducts.value) {
      try {
        const completeProduct = buildCompleteProduct(cachedBaseProduct);
        results.push(completeProduct);
      } catch (error) {
        const handle = cachedBaseProduct?.attributes?.handle || 'unknown';
        console.error(`Failed to build complete product for handle "${handle}":`, error);
      }
    }

    return results;
  });

  const findProductByHandle = (handle: string): ShopifyProductData | undefined => {
    try {
      if (!handle || typeof handle !== 'string') {
        throw new Error('Handle must be a non-empty string');
      }

      return cachedBaseProducts.value.find((product) => product?.attributes?.handle === handle);
    } catch (error) {
      console.error(`Failed to find product by handle "${handle}":`, error);
      return undefined;
    }
  };

  return {
    cachedBaseProducts,
    cachedNestedProducts,
    completeBaseProducts,
    allCachedProducts,
    setCachedBaseProducts,
    setCachedNestedProducts,
    findProductByHandle,
  };
});
